import React, { useState, useEffect } from 'react';

interface ZenClockProps {
    theme: 'dark' | 'light';
    primaryColor: string;
    logoUrl?: string;
    hospitalName: string;
}

export const ZenClock: React.FC<ZenClockProps> = ({ theme, primaryColor, logoUrl, hospitalName }) => {
    const [time, setTime] = useState(new Date());

    useEffect(() => {
        const interval = setInterval(() => {
            setTime(new Date());
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    const isDark = theme === 'dark';
    const bg = isDark ? 'bg-[#0B1121]' : 'bg-slate-50';
    const textMain = isDark ? 'text-white' : 'text-slate-800';
    const textSubtle = isDark ? 'text-slate-400' : 'text-slate-500';

    const hours = time.getHours().toString().padStart(2, '0');
    const minutes = time.getMinutes().toString().padStart(2, '0');
    const seconds = time.getSeconds().toString().padStart(2, '0');
    
    const formatter = new Intl.DateTimeFormat('es-MX', { weekday: 'long', day: 'numeric', month: 'long' });
    const dateStr = formatter.format(time);

    return (
        <div className={`w-full h-full flex flex-col items-center justify-center ${bg} overflow-hidden relative`}>
            {/* Soft Ambient Glow */}
            <div 
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60vh] h-[60vh] rounded-full opacity-20 blur-[100px] pointer-events-none"
                style={{ backgroundColor: primaryColor }}
            />

            <div className="relative z-10 flex flex-col items-center animate-fade-in fade-in-up">
                {/* Logo & Brand */}
                <div className="mb-8 flex flex-col items-center gap-4">
                    {logoUrl && logoUrl !== 'null' ? (
                        <img 
                            src={logoUrl} 
                            alt={hospitalName} 
                            className="h-16 md:h-24 object-contain filter drop-shadow-2xl" 
                        />
                    ) : (
                        <h2 className={`text-2xl md:text-3xl font-black tracking-tight ${textMain} opacity-80 uppercase`}>
                            {hospitalName}
                        </h2>
                    )}
                </div>

                {/* Clock */}
                <div className="flex items-baseline gap-2 md:gap-4 select-none">
                    <span 
                        className={`text-8xl md:text-[14rem] font-black tracking-tighter ${textMain} drop-shadow-xl`}
                        style={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                        {hours}:{minutes}
                    </span>
                    <span className={`text-4xl md:text-7xl font-light ${textSubtle}`}>
                        :{seconds}
                    </span>
                </div>

                {/* Date */}
                <div className={`mt-4 text-xl md:text-4xl font-medium ${textSubtle} capitalize tracking-wide select-none`}>
                    {dateStr}
                </div>
            </div>
            
            {/* Decorative Pulse (Optional) */}
            <div className="absolute bottom-10 flex gap-2 pointer-events-none">
                <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: primaryColor }} />
                <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: primaryColor, animationDelay: '0.2s' }} />
                <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: primaryColor, animationDelay: '0.4s' }} />
            </div>
        </div>
    );
};
